/*
 |-----------------------------------------------------------------------------
 | components/organisms/Banner/Banner.js
 | v1.0.0
 |-----------------------------------------------------------------------------
 |
 | Some description...
 |
 */

import { FC } from 'react';

import { TinaMarkdown } from 'tinacms/dist/rich-text';
import { get } from 'lodash';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
// import { ray } from 'node-ray/web';
import { useMediaQuery } from 'react-responsive';

import Head from 'next/head';
import { collectionSelector } from '@/features/collection';
import { pageSelector } from '@/features/page';
import { transform } from '@/utils/transform';
import { useAppSelector } from '@/app/hooks';

import * as IBanner from './types';
import styles from './Banner.module.scss';

const Alert = dynamic(() => import('@/molecules/Alert'));
const Breadcrumbs = dynamic(() => import('@/molecules/Breadcrumbs'));
const DateTime = dynamic(() => import('@/helpers/DateTime'));
const Picture = dynamic(() => import('@/atoms/Picture'));
const ReadingTime = dynamic(() => import('@/molecules/ReadingTime'));

const variants = {
	style1: 'style1',
	style2: 'style2',
	style3: 'style3',
	style4: 'style4',
	style5: 'style5',
};

const Banner: FC<IBanner.IProps> = ({ image, text, title, variant }) => {
	// ray('Debug organism Banner:', {
	// 	image: image,
	// 	text: text,
	// 	title: title,
	// 	variant: variant,
	// }).purple();

	const isTabletP = useMediaQuery({ query: '(max-width: 768px)' });

	const classes = classNames(get(styles, variants[variant]));

	const collection = useAppSelector(collectionSelector);
	const page = useAppSelector(pageSelector);

	let transformations = {};
	switch (variant) {
		case 'style1':
			transformations = {
				default: 'f_auto,w_1280',
				tabletL: 'f_auto,w_1024',
				tabletP: 'f_auto,w_768',
				mobileL: 'f_auto,w_414',
				mobileS: 'f_auto,w_375',
			};
			break;
		case 'style2':
			transformations = {
				default: 'f_auto,c_fill,h_515,w_1257',
				tabletL: 'f_auto,c_fill,h_515,w_1257',
				tabletP: 'f_auto,c_fill,h_515,w_1257',
				mobileL: 'f_auto,c_fill,h_515,w_1257',
				mobileS: 'f_auto,c_fill,h_515,w_1257',
			};
			break;
		case 'style5':
			transformations = {
				default: 'f_auto,w_1280',
				tabletL: 'f_auto,w_1024',
				tabletP: 'f_auto,c_thumb,w_768,h_500',
				mobileL: 'f_auto,w_414',
				mobileS: 'f_auto,w_375',
			};
			break;
	}

	const transforms = {
		default: transform(get(image, 'src'), get(transformations, 'default')),
		tabletL: transform(get(image, 'src'), get(transformations, 'tabletL')),
		tabletP: transform(get(image, 'src'), get(transformations, 'tabletP')),
		mobileL: transform(get(image, 'src'), get(transformations, 'mobileL')),
		mobileS: transform(get(image, 'src'), get(transformations, 'mobileS')),
	};

	switch (variant) {
		case 'style1':
			return (
				<>
					<Head>
						<link
							as="image"
							href={get(transforms, 'default')}
							imageSrcSet={`
								${get(transforms, 'default')} 1200w,
								${get(transforms, 'tabletL')} 1024w,
								${get(transforms, 'tabletP')} 768w,
								${get(transforms, 'mobileL')} 414w,
								${get(transforms, 'mobileS')} 375w
							`}
							rel="preload"
						/>
					</Head>
					<header className={classes}>
						{image && (
							<Picture
								alt={get(image, 'alt')}
								isBackground
								src={transforms}
							/>
						)}
						<div className={get(styles, 'foreground')}>
							<div className={get(styles, 'wrapper')}>
								{title && (
									<h1 className={get(styles, 'title')}>
										{title}
									</h1>
								)}

								{text && (
									<div className={get(styles, 'text')}>
										<TinaMarkdown content={text} />
									</div>
								)}
							</div>
						</div>
					</header>
				</>
			);
		case 'style2':
			let label, path;
			switch (collection) {
				case 'Article':
					label = 'News';
					path = '/news';
					break;
				case 'Project':
					label = 'Case studies';
					path = '/case-studies';
					break;
			}

			return (
				<>
					<Head>
						<link
							as="image"
							href={get(transforms, 'default')}
							imageSrcSet={`
							${get(transforms, 'default')} 1200w,
							${get(transforms, 'tabletL')} 1024w,
							${get(transforms, 'tabletP')} 768w,
							${get(transforms, 'mobileL')} 414w,
							${get(transforms, 'mobileS')} 375w
						`}
							rel="preload"
						/>
					</Head>

					<header className={classes}>
						{image && (
							<Picture
								alt={get(image, 'alt')}
								className="picture"
								src={transforms}
							/>
						)}

						<div className={get(styles, 'container')}>
							{!isTabletP && (
								<Breadcrumbs
									current={title}
									items={[{ label: label, path: path }]}
								/>
							)}

							<div className={get(styles, 'meta')}>
								{collection === 'Article' && (
									<span>
										<DateTime
											dateTime={get(page, 'date')}
											pattern="long"
											schema="datePublished"
										/>
									</span>
								)}

								<ReadingTime className="readingTime" />
							</div>

							{title && (
								<h1 className={get(styles, 'title')}>
									{title}
								</h1>
							)}
						</div>
					</header>
				</>
			);
		case 'style3':
		case 'style4':
			return (
				<header className={classes}>
					<div className={get(styles, 'container')}>
						{title && (
							<h1 className={get(styles, 'title')}>{title}</h1>
						)}
					</div>
				</header>
			);
		case 'style5':
			return (
				<>
					<Head>
						<link
							as="image"
							href={get(transforms, 'default')}
							imageSrcSet={`
								${get(transforms, 'default')} 1200w,
								${get(transforms, 'tabletL')} 1024w,
								${get(transforms, 'tabletP')} 768w,
								${get(transforms, 'mobileL')} 414w,
								${get(transforms, 'mobileS')} 375w
							`}
							rel="preload"
						/>
					</Head>
					<header className={classes}>
						{image && (
							<Picture
								alt={get(image, 'alt')}
								isBackground
								src={transforms}
							/>
						)}
						<div className={get(styles, 'foreground')}>
							<div className={get(styles, 'wrapper')}>
								{title && (
									<h1 className={get(styles, 'title')}>
										{title}
									</h1>
								)}

								{text && (
									<div className={get(styles, 'text')}>
										<TinaMarkdown content={text} />
									</div>
								)}
							</div>
						</div>
					</header>
				</>
			);
		default:
			return (
				<Alert
					isDismissible
					text="Please select banner variant"
					variant="warning"
				/>
			);
	}
};

export default Banner;
